import React, { useState, useEffect, useRef } from 'react';
import './ChatPanel.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const SUGGESTIONS = [
  { label: '📊 Priorités du jour', text: "Quelles sont mes priorités du jour ?" },
  { label: '⚠️ Litiges en retard', text: "Combien de litiges sont en retard ? Liste-les." },
  { label: '📥 Leads non traités', text: "Combien de leads sont en attente depuis plus de 48h ?" },
  { label: '📈 Statistiques globales', text: "Donne-moi les statistiques globales du CRM." },
  { label: '🔧 Bilan atelier', text: "Quel est le bilan de charge de l'atelier pour aujourd'hui ?" },
  { label: '📋 Rapport de clôture', text: "Génère le rapport de clôture de l'atelier pour aujourd'hui." },
];

function formatMessage(text) {
  // Formatage markdown simple
  return text
    .split('\n')
    .map((line, i) => {
      // Titres
      if (line.startsWith('### ')) return <h4 key={i} style={{ margin: '8px 0 4px', fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>{line.slice(4)}</h4>;
      if (line.startsWith('## ')) return <h3 key={i} style={{ margin: '10px 0 4px', fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>{line.slice(3)}</h3>;
      // Listes
      if (line.match(/^[-•*]\s/)) return <li key={i} style={{ marginLeft: '16px', marginBottom: '2px' }}>{formatInline(line.slice(2))}</li>;
      if (line.match(/^\d+\.\s/)) return <li key={i} style={{ marginLeft: '16px', marginBottom: '2px', listStyleType: 'decimal' }}>{formatInline(line.replace(/^\d+\.\s/, ''))}</li>;
      // Ligne vide
      if (!line.trim()) return <br key={i} />;
      // Paragraphe normal
      return <p key={i} style={{ margin: '2px 0' }}>{formatInline(line)}</p>;
    });
}

function formatInline(text) {
  // Gras : **texte**
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

export default function ChatPanel({ isOpen, onClose, user }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  const sendMessage = async (text) => {
    const messageText = (text || input).trim();
    if (!messageText || loading) return;

    const newUserMsg = { role: 'user', content: messageText };
    const updatedMessages = [...messages, newUserMsg];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const history = updatedMessages
        .slice(-10)
        .map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.content }));

      const res = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: messageText, history: history.slice(0, -1) })
      });

      const data = await res.json();

      if (res.ok) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: `❌ ${data.error || 'Erreur lors du traitement.'}`, isError: true }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: '❌ Impossible de contacter le serveur.', isError: true }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleReset = () => {
    setMessages([]);
    setInput('');
  };

  return (
    <>
      {/* Overlay transparent pour fermer en cliquant à côté */}
      {isOpen && <div className="chat-panel-overlay" onClick={onClose} />}

      <div className={`chat-panel ${isOpen ? 'chat-panel-open' : ''}`}>
        {/* Header */}
        <div className="chat-panel-header">
          <div className="chat-panel-title">
            <span className="chat-panel-icon">🤖</span>
            <div>
              <div className="chat-panel-name">Assistant CRM</div>
              <div className="chat-panel-subtitle">Powered by AI</div>
            </div>
          </div>
          <div className="chat-panel-actions">
            {messages.length > 0 && (
              <button className="chat-action-btn" onClick={handleReset} title="Nouvelle conversation">
                🗑️
              </button>
            )}
            <button className="chat-action-btn" onClick={onClose} title="Fermer">
              ✕
            </button>
          </div>
        </div>

        {/* Corps des messages */}
        <div className="chat-messages">
          {messages.length === 0 && !loading && (
            <div className="chat-welcome">
              <div className="chat-welcome-emoji">🤖</div>
              <div className="chat-welcome-text">
                Bonjour {user?.prenom || ''} ! Je peux répondre à toutes vos questions sur les données du CRM.
              </div>
              <div className="chat-suggestions">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    className="chat-suggestion-chip"
                    onClick={() => sendMessage(s.text)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`chat-message chat-message-${msg.role} ${msg.isError ? 'chat-message-error' : ''}`}>
              {msg.role === 'assistant' && (
                <div className="chat-message-avatar">🤖</div>
              )}
              <div className="chat-message-bubble">
                {msg.role === 'assistant'
                  ? <div className="chat-message-content">{formatMessage(msg.content)}</div>
                  : <div className="chat-message-content">{msg.content}</div>
                }
              </div>
            </div>
          ))}

          {loading && (
            <div className="chat-message chat-message-assistant">
              <div className="chat-message-avatar">🤖</div>
              <div className="chat-message-bubble">
                <div className="chat-typing">
                  <span /><span /><span />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="chat-input-area">
          <textarea
            ref={inputRef}
            className="chat-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Posez votre question… (Entrée pour envoyer)"
            rows={2}
            disabled={loading}
          />
          <button
            className="chat-send-btn"
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
          >
            {loading ? '⏳' : '➤'}
          </button>
        </div>
      </div>
    </>
  );
}
